import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { FileText, ExternalLink, ShieldCheck, XCircle, X, Loader2, DollarSign, RefreshCw, Sparkles, RotateCcw, MapPin } from "lucide-react";

/**
 * Admin · Certificate of Authorisation issuance.
 *
 * Two-column layout: left = eligible partners (buyer/seller Level-3), right =
 * draft/published certificate detail with Generate → Preview → Publish flow.
 *
 * Publishing pushes the PDF to Object Storage, drops it into the buyer's
 * Document Vault, generates a fresh Razorpay Payment Link, emails a
 * ₹41,680 processing-fee intimation with the pay link inline, and marks the
 * certificate `published`.
 */
export function AdminCertificates({ api }) {
  const [users, setUsers] = useState([]);
  const [certs, setCerts] = useState([]);
  const [statesList, setStatesList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [directors, setDirectors] = useState("");
  const [pickedStates, setPickedStates] = useState([]);   // multi-select "Authorized States"
  const [validDays, setValidDays] = useState(365);
  const [busy, setBusy] = useState(false);
  const [reissuing, setReissuing] = useState(false);
  const [feeBreakup, setFeeBreakup] = useState(null);

  const MAX_STATES = feeBreakup?.max_states || 5;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [u, c, s] = await Promise.all([
        api.get("/users"),
        api.get("/admin/certificates"),
        api.get("/states").catch(() => ({ data: [] })),
      ]);
      const eligible = (u.data || []).filter((x) => x.role !== "admin" && x.status === "active");
      setUsers(eligible);
      setCerts(c.data || []);
      setStatesList(s.data || []);
    } catch { toast.error("Could not load partners / certificates"); }
    finally { setLoading(false); }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  // Prefill "Authorized States" from the partner's onboarded coverage list
  // (falling back to their registered state) whenever a new partner is selected.
  useEffect(() => {
    if (!selected) return;
    const prof = selected?.profile || {};
    const pref = Array.isArray(prof.authorized_states) && prof.authorized_states.length
      ? prof.authorized_states
      : (prof.state ? [prof.state] : []);
    setPickedStates(pref.slice(0, 5));
    setDirectors("");
    setValidDays(365);
  }, [selected]);

  // Live fee calculator — hit backend so pricing stays server-authoritative.
  useEffect(() => {
    const n = Math.max(1, pickedStates.length || 1);
    let dead = false;
    api.get(`/certificates/fee-calc?states=${n}`)
      .then((r) => { if (!dead) setFeeBreakup(r.data); })
      .catch(() => { if (!dead) setFeeBreakup(null); });
    return () => { dead = true; };
  }, [api, pickedStates.length]);

  const certByUser = certs.reduce((acc, c) => {
    if (c.status !== "revoked") acc[c.user_id] = c;
    return acc;
  }, {});
  const activeCert = selected ? certByUser[selected.id] : null;

  const generate = async () => {
    if (!selected) return;
    if (pickedStates.length < 1) { toast.error("Select at least one Authorized State"); return; }
    if (pickedStates.length > MAX_STATES) { toast.error(`Maximum ${MAX_STATES} states allowed`); return; }
    setBusy(true);
    try {
      const r = await api.post(`/admin/certificates/${selected.id}/generate`, {
        directors: directors.split(",").map((s) => s.trim()).filter(Boolean),
        territories: pickedStates,
        valid_days: Number(validDays) || 365,
      });
      toast.success(`Draft ready · ${r.data.certificate_number}`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Generate failed"); }
    finally { setBusy(false); }
  };

  const toggleState = (s) => {
    setPickedStates((prev) => {
      if (prev.includes(s)) return prev.filter((x) => x !== s);
      if (prev.length >= MAX_STATES) { toast.warning(`Maximum ${MAX_STATES} states allowed`); return prev; }
      return [...prev, s];
    });
  };

  const reissueAll = async () => {
    if (!window.confirm("Reissue ALL existing certificates with the new FFPMC Florida, USA format?\n\nThis will overwrite the PDF in Object Storage for every published certificate. Certificate numbers, sponsor and territory data stay the same. This cannot be undone.")) return;
    setReissuing(true);
    try {
      const r = await api.post("/admin/certificates/reissue-all");
      toast.success(`${r.data.reissued} certificate(s) re-issued`);
      if ((r.data.failed || []).length) {
        toast.warning(`${r.data.failed.length} failed — see admin log`);
      }
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Reissue failed"); }
    finally { setReissuing(false); }
  };

  const publish = async (certId) => {
    const activeFee = (activeCert?.fee_inr || activeCert?.processing_fee_inr || 41680).toLocaleString("en-IN");
    if (!window.confirm(`Publish this certificate?\n\nFresh PhonePe + Razorpay payment links for ₹${activeFee} will be generated and emailed to the partner along with the certificate. The PDF will land in their Document Vault.`)) return;
    setBusy(true);
    try {
      await api.post(`/admin/certificates/${certId}/publish`);
      toast.success("Certificate published · fee email sent");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Publish failed"); }
    finally { setBusy(false); }
  };

  const revoke = async (certId) => {
    if (!window.confirm("Revoke this certificate? This cannot be undone.")) return;
    try {
      await api.post(`/admin/certificates/${certId}/revoke`);
      toast.success("Certificate revoked");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Revoke failed"); }
  };

  const markPaid = async (certId) => {
    const activeFee = (activeCert?.fee_inr || activeCert?.processing_fee_inr || 41680).toLocaleString("en-IN");
    const note = window.prompt(
      `Mark this certificate fee as received (₹${activeFee}).\n\nOptional reference (NEFT ID, cheque #, etc.):`,
      ""
    );
    if (note === null) return;
    try {
      await api.post(`/admin/certificates/${certId}/mark-paid`,
                     note.trim() ? { payment_id: note.trim() } : {});
      toast.success("Certificate marked as paid · buyer notified");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Mark-paid failed"); }
  };

  const backend = process.env.REACT_APP_BACKEND_URL;

  if (loading) return <div className="p-8 text-center font-body text-sm text-[#8A8A82]">Loading…</div>;

  return (
    <div data-testid="admin-certificates" className="grid lg:grid-cols-[380px_1fr] gap-6">
      {/* Left · partner list */}
      <div className="border border-white/10 rounded-sm bg-[#0a0a0a] overflow-hidden">
        <div className="px-4 py-3 border-b border-white/[0.06] flex items-center justify-between">
          <span className="font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#8A8A82]">Eligible partners</span>
          <div className="flex items-center gap-3">
            <button data-testid="cert-reissue-all" type="button" onClick={reissueAll} disabled={reissuing}
                    title="Regenerate every published certificate with the new FFPMC Florida, USA format"
                    className="text-[10px] uppercase tracking-[0.2em] font-grotesk text-[#B87333] hover:text-[#D8B23E] inline-flex items-center gap-1 disabled:opacity-50">
              {reissuing ? <Loader2 size={11} className="animate-spin"/> : <RotateCcw size={11}/>}
              Reissue all
            </button>
            <button data-testid="cert-refresh" type="button" onClick={load} className="text-[#8A8A82] hover:text-[#B87333]">
              <RefreshCw size={12} />
            </button>
          </div>
        </div>
        <ul className="max-h-[70vh] overflow-y-auto">
          {users.map((u) => {
            const cert = certByUser[u.id];
            const badge = cert
              ? cert.status === "published"
                ? { label: "Published", color: "#2FB999" }
                : { label: "Draft", color: "#B87333" }
              : null;
            const active = selected?.id === u.id;
            return (
              <li key={u.id} data-testid={`cert-partner-${u.id}`}
                  onClick={() => setSelected(u)}
                  className={`px-4 py-3 border-b border-white/[0.04] cursor-pointer transition-colors ${active ? "bg-[#B87333]/[0.08]" : "hover:bg-white/[0.03]"}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="font-serif text-sm text-[#F5F5F0] truncate">{u.business_name || u.email}</div>
                    <div className="font-grotesk text-[9px] uppercase tracking-[0.2em] text-[#8A8A82] mt-0.5 truncate">
                      {u.buyer_category || u.account_type} · {u.vendor_code || "—"}
                    </div>
                  </div>
                  {badge && (
                    <span className="font-grotesk text-[9px] uppercase tracking-[0.2em] px-1.5 py-0.5 rounded-sm border shrink-0"
                          style={{ color: badge.color, borderColor: badge.color + "60", background: badge.color + "10" }}>
                      {badge.label}
                    </span>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </div>

      {/* Right · issuance panel */}
      <div>
        {!selected ? (
          <div className="border border-dashed border-white/10 rounded-sm p-12 text-center bg-[#0a0a0a]">
            <FileText size={22} className="text-[#8A8A82] mx-auto mb-3" />
            <p className="font-serif text-xl text-[#F5F5F0]">Select a partner to issue their Certificate of Authorisation.</p>
            <p className="font-body text-xs text-[#8A8A82] mt-2">Vinci Inc. and FFPMC Florida, USA · Sole Issuing Authority</p>
          </div>
        ) : (
          <div className="space-y-5">
            <div className="border border-white/10 rounded-sm bg-[#0a0a0a] p-5">
              <div className="flex items-center gap-2 mb-1">
                <ShieldCheck size={16} className="text-[#B87333]" />
                <div className="font-serif text-2xl text-[#F5F5F0]">{selected.business_name || selected.email}</div>
              </div>
              <div className="font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#8A8A82]">
                {(selected.buyer_category || selected.account_type || "").toUpperCase()} · {selected.vendor_code} · GSTIN {selected.gst_number || "—"}
              </div>
              {selected.parent_business_name && (
                <div className="mt-2 font-body text-xs text-[#C9C9C2]">
                  Sponsor: <span className="text-[#B87333]">{selected.parent_business_name}</span>
                  {selected.parent_category ? ` (${selected.parent_category.charAt(0).toUpperCase() + selected.parent_category.slice(1)})` : ""}
                </div>
              )}
            </div>

            {/* Draft form */}
            {(!activeCert || activeCert.status === "draft") && (
              <div className="border border-white/10 rounded-sm bg-[#0a0a0a] p-5 space-y-3">
                <div className="font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#8A8A82] flex items-center gap-2">
                  <Sparkles size={12} className="text-[#B87333]" /> Draft the certificate
                </div>
                <div>
                  <label className="font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#8A8A82] block mb-1">
                    Directors / Authorised Representatives <span className="text-[#8A8A82]">· comma-separated</span>
                  </label>
                  <input data-testid="cert-directors" value={directors} onChange={(e) => setDirectors(e.target.value)}
                         placeholder="Rakesh Verma, Anita Rao"
                         className="w-full bg-black/40 border border-white/15 rounded-sm px-3 py-2 font-body text-sm text-[#F5F5F0] focus:outline-none focus:border-[#B87333]" />
                </div>
                <div>
                  <div className="flex items-baseline justify-between mb-1">
                    <label className="font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#8A8A82]">
                      Authorized States <span className="text-[#8A8A82]">· min 1 · max {MAX_STATES}</span>
                    </label>
                    <span className="font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#B87333]" data-testid="cert-states-count">
                      {pickedStates.length}/{MAX_STATES} selected
                    </span>
                  </div>
                  {pickedStates.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-2" data-testid="cert-picked-states">
                      {pickedStates.map((s) => (
                        <button key={s} type="button" onClick={() => toggleState(s)}
                                data-testid={`cert-picked-${s.replace(/\s+/g, "-")}`}
                                className="inline-flex items-center gap-1 border border-[#B87333] text-[#B87333] bg-[#B87333]/10 rounded-full px-2.5 py-1 text-[10px] font-grotesk">
                          <MapPin size={10}/> {s} <X size={10}/>
                        </button>
                      ))}
                    </div>
                  )}
                  <div className="max-h-40 overflow-y-auto border border-white/10 rounded-sm p-2 bg-black/40" data-testid="cert-states-picker">
                    <div className="flex flex-wrap gap-1.5">
                      {statesList.map((s) => {
                        const active = pickedStates.includes(s);
                        const atCap = !active && pickedStates.length >= MAX_STATES;
                        return (
                          <button key={s} type="button" disabled={atCap}
                                  onClick={() => toggleState(s)}
                                  data-testid={`cert-state-opt-${s.replace(/\s+/g, "-")}`}
                                  className={`rounded-full px-2.5 py-1 text-[10px] font-grotesk border transition-colors ${
                                    active
                                      ? "border-[#B87333] text-[#B87333] bg-[#B87333]/10"
                                      : atCap
                                        ? "border-white/5 text-white/20 cursor-not-allowed"
                                        : "border-white/10 text-[#C9C9C2] hover:border-white/30"
                                  }`}>
                            {active ? "✓ " : ""}{s}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div className="font-body text-[10px] text-[#8A8A82] mt-1">Prefilled from partner’s onboarded coverage list. Every additional state is offered at a flat <span className="text-[#D8B23E]">50% off the base rate</span>.</div>
                </div>
                {feeBreakup && (
                  <FeeBreakupCard breakup={feeBreakup} testid="cert-fee-breakup" />
                )}
                <div>
                  <label className="font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#8A8A82] block mb-1">Validity (days)</label>
                  <input data-testid="cert-validity" type="number" value={validDays} onChange={(e) => setValidDays(e.target.value)} min="30" max="1095"
                         className="w-full max-w-[200px] bg-black/40 border border-white/15 rounded-sm px-3 py-2 font-body text-sm text-[#F5F5F0] focus:outline-none focus:border-[#B87333]" />
                </div>
                <button data-testid="cert-generate" type="button" onClick={generate} disabled={busy}
                        className="bg-[#B87333] hover:bg-[#A05F26] text-black font-grotesk text-[10px] uppercase tracking-[0.24em] px-5 py-2.5 rounded-sm inline-flex items-center gap-2 disabled:opacity-50">
                  {busy ? <Loader2 size={12} className="animate-spin"/> : <FileText size={12}/>}
                  {activeCert ? "Regenerate draft" : "Generate certificate"}
                </button>
              </div>
            )}

            {/* Draft / published card */}
            {activeCert && (
              <AnimatePresence>
                <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                    className={`border rounded-sm p-5 ${activeCert.status === "published" ? "border-[#2FB999]/40 bg-[#2FB999]/[0.04]" : "border-[#B87333]/40 bg-[#B87333]/[0.04]"}`}>
                  <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
                    <div>
                      <div className="font-grotesk text-[10px] uppercase tracking-[0.22em]" style={{ color: activeCert.status === "published" ? "#2FB999" : "#B87333" }}>
                        {activeCert.status === "published" ? "Live · in partner vault" : "Draft · awaiting publish"}
                      </div>
                      <div className="font-serif text-xl text-[#F5F5F0] mt-1">{activeCert.certificate_number}</div>
                      <div className="font-body text-xs text-[#8A8A82] mt-1">
                        Valid until {new Date(activeCert.expires_at).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })} · {activeCert.state_count || (activeCert.authorized_states?.length || 1)} state(s) · fee ₹{(activeCert.processing_fee_inr || activeCert.fee_inr || 41680).toLocaleString("en-IN")}
                      </div>
                      {(activeCert.authorized_states || []).length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2" data-testid={`cert-states-list-${activeCert.id}`}>
                          {(activeCert.authorized_states || []).map((s) => (
                            <span key={s} className="inline-flex items-center gap-1 rounded-full border border-white/10 text-[#C9C9C2] px-2 py-0.5 text-[9px] font-grotesk">
                              <MapPin size={9}/>{s}
                            </span>
                          ))}
                        </div>
                      )}
                      {activeCert.payment_link_url && (
                        <a href={activeCert.payment_link_url} target="_blank" rel="noreferrer"
                           data-testid={`cert-paylink-${activeCert.id}`}
                           className="font-grotesk text-[10px] uppercase tracking-[0.2em] text-[#2FB999] hover:text-[#28a086] mt-2 inline-flex items-center gap-1">
                          <DollarSign size={11}/> Razorpay pay link
                          <ExternalLink size={10}/>
                        </a>
                      )}
                      {activeCert.status === "published" && (
                        <>
                          <span data-testid={`cert-fee-status-${activeCert.id}`}
                                className="font-grotesk text-[10px] uppercase tracking-[0.2em] px-2 py-1 rounded-sm border mt-2 inline-block ml-0"
                                style={{ color: activeCert.fee_status === "paid" ? "#2FB999" : "#D8B23E",
                                         borderColor: activeCert.fee_status === "paid" ? "#2FB99966" : "#D8B23E66" }}>
                            Fee · {activeCert.fee_status === "paid" ? "Paid" : "Pending"}
                          </span>
                          {activeCert.fee_paid_at && (
                            <span className="font-grotesk text-[10px] uppercase tracking-[0.18em] text-[#8A8A82] ml-2">
                              on {new Date(activeCert.fee_paid_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                            </span>
                          )}
                        </>
                      )}
                    </div>
                    <div className="flex gap-2 shrink-0 flex-wrap">
                      <a data-testid={`cert-preview-${activeCert.id}`}
                         href={`${backend}/api/admin/certificates/${activeCert.id}/preview.pdf`} target="_blank" rel="noreferrer"
                         className="border border-white/20 hover:border-[#B87333] font-grotesk text-[10px] uppercase tracking-[0.22em] px-4 py-2 rounded-sm text-[#C9C9C2] hover:text-[#B87333] inline-flex items-center gap-1.5">
                        Preview PDF <ExternalLink size={11}/>
                      </a>
                      {activeCert.status === "draft" && (
                        <button data-testid={`cert-publish-${activeCert.id}`} type="button" onClick={() => publish(activeCert.id)} disabled={busy}
                                className="bg-[#2FB999] hover:bg-[#28a086] text-black font-grotesk text-[10px] uppercase tracking-[0.24em] px-5 py-2 rounded-sm inline-flex items-center gap-1.5 disabled:opacity-50">
                          <DollarSign size={12}/> Publish & email fee
                        </button>
                      )}
                      {activeCert.status === "published" && activeCert.fee_status !== "paid" && (
                        <button data-testid={`cert-mark-paid-${activeCert.id}`} type="button" onClick={() => markPaid(activeCert.id)}
                                className="bg-[#D8B23E] hover:bg-[#c39d2f] text-black font-grotesk text-[10px] uppercase tracking-[0.24em] px-5 py-2 rounded-sm inline-flex items-center gap-1.5">
                          <DollarSign size={12}/> Mark as paid
                        </button>
                      )}
                      {activeCert.status === "published" && (
                        <button data-testid={`cert-revoke-${activeCert.id}`} type="button" onClick={() => revoke(activeCert.id)}
                                className="border border-white/20 hover:border-[#FF5A5F] hover:text-[#FF5A5F] font-grotesk text-[10px] uppercase tracking-[0.22em] px-4 py-2 rounded-sm text-[#C9C9C2] inline-flex items-center gap-1.5">
                          <XCircle size={11}/> Revoke
                        </button>
                      )}
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>
            )}
          </div>
        )}
      </div>
    </div>
  );
}


/**
 * Real-time fee breakup card — obsidian panel that walks the customer through
 * base + per-additional-state pricing (each additional state is 50% off the
 * base rate) and lands on the final Total Certification Fee.
 */
function FeeBreakupCard({ breakup, testid }) {
  const rupee = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
  const isMulti = (breakup.additional_states || 0) > 0;
  return (
    <div data-testid={testid || "cert-fee-breakup"}
         className="relative overflow-hidden border border-[#B87333]/30 rounded-sm p-4 bg-gradient-to-br from-[#0e0a06] via-[#0a0a0a] to-[#0e0a06]">
      <div className="absolute inset-0 pointer-events-none opacity-[0.06]"
           style={{ backgroundImage: "radial-gradient(circle at 20% 0%, #D8B23E 0%, transparent 50%)" }} />
      <div className="relative">
        <div className="flex items-baseline justify-between mb-3">
          <span className="font-grotesk text-[9px] uppercase tracking-[0.24em] text-[#8A8A82]">Total Certification Fee</span>
          {isMulti && (
            <span className="font-grotesk text-[9px] uppercase tracking-[0.24em] text-[#D8B23E]" data-testid="cert-fee-discount-badge">
              ✦ Multi-state · {breakup.discount_pct}% off
            </span>
          )}
        </div>
        <div className="space-y-1.5 font-body text-[12px] text-[#C9C9C2]">
          <div className="flex justify-between">
            <span>Base authorisation · 1st state</span>
            <span className="tabular-nums text-[#F5F5F0]" data-testid="cert-fee-base">{rupee(breakup.base_fee_inr)}</span>
          </div>
          {isMulti ? (
            <div className="flex justify-between">
              <span>Additional {breakup.additional_states} state(s) × <span className="text-[#D8B23E]">{rupee(breakup.per_additional_state_inr)}</span> <span className="text-[#8A8A82]">(50% off base)</span></span>
              <span className="tabular-nums text-[#F5F5F0]" data-testid="cert-fee-add">{rupee(breakup.additional_full_inr)}</span>
            </div>
          ) : (
            <div className="text-[10px] text-[#8A8A82] italic pt-0.5">
              Add a 2nd Authorized State to unlock the <span className="text-[#D8B23E] not-italic font-semibold">50% off</span> multi-state rate.
            </div>
          )}
        </div>
        <div className="mt-3 pt-3 border-t border-white/10 flex items-baseline justify-between">
          <span className="font-grotesk text-[10px] uppercase tracking-[0.24em] text-[#B87333]">Payable now</span>
          <span className="font-serif text-2xl text-[#F5F5F0] tabular-nums" data-testid="cert-fee-total">{rupee(breakup.total_inr)}</span>
        </div>
      </div>
    </div>
  );
}
export { FeeBreakupCard };
